// src/hooks/useImageUpload.js
import { useState, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';
import { useNumberValidation } from './useNumberValidation';

export const useImageUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Use the imported number validation hook
  const { validateNumberInput } = useNumberValidation();

  // Function to generate unique file names
  const generateFileName = (originalName) => {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExtension = originalName.split('.').pop();
    return `${timestamp}_${randomString}.${fileExtension}`;
  };

  // Function to validate file
  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      throw new Error(`نوع الملف غير مدعوم: ${file.type}`);
    }

    if (file.size > maxSize) {
      throw new Error(`حجم الملف كبير جداً: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    }

    return true;
  };

  // Function to compress image if needed
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        try {
          // Calculate new dimensions
          let { width, height } = img;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('فشل في ضغط الصورة'));
            }
          }, 'image/jpeg', quality);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error('فشل في تحميل الصورة للضغط'));
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Function to upload a single image
  const uploadSingleImage = useCallback(async (file, bucket = 'products') => {
    try {
      console.log('Starting upload for file:', file.name, 'Size:', file.size);
      
      // Validate file
      validateFile(file);
      
      // Compress image if it's too large
      let fileToUpload = file;
      if (file.size > 1024 * 1024) { // If larger than 1MB
        console.log('Compressing image...');
        fileToUpload = await compressImage(file);
        console.log('Compressed size:', fileToUpload.size);
      }
      
      const fileName = generateFileName(file.name);
      const filePath = `${bucket}/${fileName}`;
      
      console.log('Uploading to path:', filePath);
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(filePath, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        console.error('Supabase upload error:', error);
        throw new Error(`فشل في رفع الملف: ${error.message}`);
      }
      
      console.log('Upload successful:', data);
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);
      
      console.log('Public URL:', publicUrl);
      
      // Verify the URL is accessible
      try {
        const response = await fetch(publicUrl, { method: 'HEAD' });
        if (!response.ok) {
          throw new Error(`الرابط غير صالح: ${response.status}`);
        }
      } catch (fetchError) {
        console.warn('URL verification failed:', fetchError);
        // Continue anyway, sometimes HEAD requests fail but the image is still accessible
      }
      
      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }, []);

  // Function to upload multiple images
  const handleUploadImages = useCallback(async (files, bucket = 'products') => {
    if (!files || files.length === 0) {
      console.log('No files to upload');
      return [];
    }

    console.log('Starting batch upload for', files.length, 'files');
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const uploadedUrls = [];
      const errors = [];
      const totalFiles = files.length;
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        console.log(`Uploading file ${i + 1}/${totalFiles}:`, file.name);
        
        try {
          const url = await uploadSingleImage(file, bucket);
          uploadedUrls.push(url);
          console.log(`File ${i + 1} uploaded successfully:`, url);
        } catch (error) {
          console.error(`Error uploading file ${i + 1} (${file.name}):`, error);
          errors.push(`${file.name}: ${error.message}`);
        }
        
        // Update progress
        setUploadProgress(((i + 1) / totalFiles) * 100);
      }
      
      console.log('Batch upload completed. Successful:', uploadedUrls.length, 'Errors:', errors.length);
      
      if (errors.length > 0 && uploadedUrls.length === 0) {
        throw new Error(`فشل في رفع جميع الملفات:\n${errors.join('\n')}`);
      }
      
      if (errors.length > 0) {
        console.warn('Some files failed to upload:', errors);
      }
      
      return uploadedUrls;
    } catch (error) {
      console.error('Error in batch upload:', error);
      throw error;
    } finally {
      setUploading(false);
      // Keep progress at 100% for a moment before resetting
      setTimeout(() => {
        setUploadProgress(0);
      }, 1000);
    }
  }, [uploadSingleImage]);

  // Function to delete image from storage
  const deleteImage = useCallback(async (imageUrl, bucket = 'products') => {
    try {
      console.log('Deleting image:', imageUrl);
      
      // Extract file path from URL
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const filePath = `${bucket}/${fileName}`;
      
      console.log('Deleting file path:', filePath);
      
      const { error } = await supabase.storage
        .from(bucket)
        .remove([filePath]);
      
      if (error) {
        console.error('Error deleting image:', error);
        throw new Error(`فشل في حذف الصورة: ${error.message}`);
      }
      
      console.log('Image deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }, []);

  // Function to delete multiple images
  const deleteMultipleImages = useCallback(async (imageUrls, bucket = 'products') => {
    try {
      console.log('Deleting multiple images:', imageUrls.length);
      
      const deletePromises = imageUrls.map(url => deleteImage(url, bucket));
      const results = await Promise.allSettled(deletePromises);
      
      const failures = results.filter(result => result.status === 'rejected');
      if (failures.length > 0) {
        console.error('Some deletions failed:', failures);
        throw new Error(`فشل في حذف ${failures.length} من ${imageUrls.length} صور`);
      }
      
      console.log('All images deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting multiple images:', error);
      throw error;
    }
  }, [deleteImage]);

  // Function to reorder images (for drag and drop)
  const reorderImages = useCallback((images, fromIndex, toIndex) => {
    const reorderedImages = [...images];
    const [movedItem] = reorderedImages.splice(fromIndex, 1);
    reorderedImages.splice(toIndex, 0, movedItem);
    return reorderedImages;
  }, []);

  // Function to check if Supabase is properly configured
  const checkSupabaseConnection = useCallback(async () => {
    try {
      const { data, error } = await supabase.storage.listBuckets();
      if (error) {
        console.error('Supabase connection error:', error);
        return false;
      }
      console.log('Supabase connection successful. Buckets:', data);
      return true;
    } catch (error) {
      console.error('Supabase connection failed:', error);
      return false;
    }
  }, []);

  return {
    uploading,
    uploadProgress,
    handleUploadImages,
    uploadSingleImage,
    deleteImage,
    deleteMultipleImages,
    reorderImages,
    checkSupabaseConnection,
    // Expose the number validation function from the imported hook
    validateNumberInput
  };
};
